import React, { useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { RootState } from '../store';
import { socketService } from '../services/socket';
import { AgentUser } from '../types';
import Grid from '@mui/material/Grid2';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Chip,
  Avatar,
  AvatarGroup,
  useTheme,
  alpha,
  LinearProgress,
  Stack,
  Divider,
  IconButton,
  Tooltip,
  Paper,
  Container,
  Badge,
} from '@mui/material';
import {
  People,
  Person,
  AccessTime,
  TrendingUp,
  CheckCircle,
  Refresh,
  Circle,
  SignalCellularAlt,
  Speed,
  Timer,
  Groups,
  Notifications,
  Message,
  Support,
  ConfirmationNumber,
  Business,
  Assignment,
  BugReport,
  PriorityHigh,
} from '@mui/icons-material';
import { motion } from 'framer-motion';
import { GlassCard } from '../components/ui/GlassCard';
import { format, differenceInMinutes, differenceInHours, differenceInDays } from 'date-fns';
import { useLayout } from '../contexts/LayoutContext';
import { showInstallPrompt } from '../components/InstallPrompt';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip as RechartsTooltip,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
  Legend,
  LineChart,
  Line,
  Area,
  AreaChart,
} from 'recharts';

interface DashboardStats {
  onlineUsers: number;
  totalUsers: number;
  lastCheckIn: Date | null;
  activeChats: number;
  pendingTickets: number;
  todayMessages: number;
  responseTime: number; // in minutes
  uptime: number; // percentage
  tickets?: {
    total: number;
    open: number;
    inProgress: number;
    closed: number;
    byType: Record<string, number>;
    byClient: Record<string, number>;
    byPriority: Record<string, number>;
  };
  clients?: number;
  activeUsers?: number;
}

const Dashboard: React.FC = () => {
  const theme = useTheme();
  const { preferences } = useLayout();
  const isSidebarLayout = preferences.layoutMode === 'sidebar';
  const user = useSelector((state: RootState) => state.auth.user);
  const users = useSelector((state: RootState) => state.chat?.users || []);
  const activeChats = useSelector((state: RootState) => state.chat?.activeChats || []);
  const lastCheckIn = useSelector((state: RootState) => state.auth?.lastCheckIn);
  const isConnected = useSelector((state: RootState) => state.connection.isConnected);
  
  const [stats, setStats] = useState<DashboardStats>({
    onlineUsers: 0,
    totalUsers: 0,
    lastCheckIn: null,
    activeChats: 0,
    pendingTickets: 0,
    todayMessages: 0,
    responseTime: 0,
    uptime: 99.9,
  });
  const [recentUsers, setRecentUsers] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);

  // Show install prompt when dashboard loads
  useEffect(() => {
    // Small delay to ensure it doesn't interfere with navigation
    const timer = setTimeout(() => {
      showInstallPrompt();
    }, 1000);
    
    return () => clearTimeout(timer);
  }, []);

  // Update stats when Redux data changes (except lastCheckIn which changes too frequently)
  useEffect(() => {
    const onlineUsersList = users ? users.filter((u: AgentUser) => u.isOnline) : [];
    const activeChatCount = Array.isArray(activeChats) ? activeChats.length : 0;
    
    // Convert online users to display format
    const displayUsers = onlineUsersList.slice(0, 5).map((u: AgentUser) => ({
      name: u.fullName,
      email: u.email,
      status: u.isOnline ? 'online' : 'offline',
      avatar: u.fullName.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2),
      company: u.agentName,
    }));
    
    setRecentUsers(displayUsers);
    
    setStats(prev => ({
      ...prev,
      onlineUsers: onlineUsersList.length,
      totalUsers: users ? users.length : 0,
      activeChats: activeChatCount,
      uptime: isConnected ? 99.9 : 0,
    }));
    
    setLoading(false);
  }, [users?.length, activeChats?.length, isConnected]);
  
  // Update lastCheckIn separately to avoid frequent re-renders
  useEffect(() => {
    setStats(prev => ({
      ...prev,
      lastCheckIn: lastCheckIn ? new Date(lastCheckIn) : null,
    }));
  }, [lastCheckIn]);

  // Set up socket listeners once
  useEffect(() => {
    const socket = socketService.getSocket();
    if (!socket) return;

    // Listen for real-time updates
    const handleUserStatusUpdate = (data: any) => {
      console.log('User status update:', data);
    };

    const handleStatsUpdate = (data: any) => {
      setStats(prev => ({ ...prev, ...data }));
    };

    socket.on('userStatusUpdate', handleUserStatusUpdate);
    socket.on('statsUpdate', handleStatsUpdate);
    
    // Request dashboard stats from backend
    socket.emit('getDashboardStats', {}, (response: any) => {
      if (response.success && response.data) {
        const { tickets, clients, activeUsers } = response.data;
        setStats(prev => ({ 
          ...prev,
          tickets,
          clients,
          activeUsers,
          pendingTickets: tickets?.open || 0,
          todayMessages: prev.todayMessages,
          responseTime: prev.responseTime,
        }));
      }
    });
    
    // Also request fresh legacy stats if available
    socket.emit('getStats', {}, (response: any) => {
      if (response.success) {
        setStats(prev => ({ 
          ...prev, 
          ...response.stats,
          todayMessages: response.stats?.todayMessages || prev.todayMessages,
          responseTime: response.stats?.responseTime || prev.responseTime,
        }));
      }
    });

    return () => {
      socket.off('userStatusUpdate', handleUserStatusUpdate);
      socket.off('statsUpdate', handleStatsUpdate);
    };
  }, []); // Only run once on mount

  const getTimeSinceCheckIn = () => {
    if (!stats.lastCheckIn) return 'Never';
    const minutes = differenceInMinutes(new Date(), stats.lastCheckIn);
    if (minutes < 1) return 'Just now';
    if (minutes < 60) return `${minutes} minute${minutes !== 1 ? 's' : ''} ago`;
    const hours = differenceInHours(new Date(), stats.lastCheckIn);
    if (hours < 24) return `${hours} hour${hours !== 1 ? 's' : ''} ago`;
    const days = differenceInDays(new Date(), stats.lastCheckIn);
    return `${days} day${days !== 1 ? 's' : ''} ago`;
  };

  const StatCard = ({ icon, title, value, subtitle, color, trend }: any) => (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.3 }}
    >
      <GlassCard gradient>
        <Box sx={{ p: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between' }}>
            <Box sx={{ flex: 1 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mb: 1.5 }}>
                <Box
                  sx={{
                    p: 0.75,
                    borderRadius: 2,
                    background: `linear-gradient(135deg, ${color} 0%, ${alpha(color, 0.7)} 100%)`,
                    color: '#fff',
                    display: 'flex',
                  }}
                >
                  {icon}
                </Box>
                <Typography variant="body2" color="text.secondary">
                  {title}
                </Typography>
              </Box>
              <Typography variant="h4" fontWeight={700} sx={{ mb: 0.5 }}>
                {value}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {subtitle}
              </Typography>
            </Box>
            {trend && (
              <Chip
                size="small"
                label={trend}
                sx={{
                  backgroundColor: alpha(theme.palette.success.main, 0.1),
                  color: theme.palette.success.main,
                  fontWeight: 600,
                }}
                icon={<TrendingUp sx={{ fontSize: 16 }} />}
              />
            )}
          </Box>
        </Box>
      </GlassCard>
    </motion.div>
  );

  return (
    <Container maxWidth="xl" sx={{ py: 3 }}>
      {/* Welcome Section */}
      <Box sx={{ 
        mb: 4,
        mt: -3,  // Negative margin to extend to top
        pt: 3,   // Add padding back to keep content position
        // Only make draggable in sidebar mode
        ...(isSidebarLayout && {
          WebkitAppRegion: 'drag',
          '-webkit-app-region': 'drag',
        })
      }}>
        <Typography 
          variant="h4" 
          fontWeight={700} 
          gutterBottom
          sx={{
            // Make text selectable, not draggable
            display: 'inline-block',
            userSelect: 'text',
            WebkitAppRegion: 'no-drag',
            '-webkit-app-region': 'no-drag',
          }}
        >
          Welcome back, {user?.name || user?.email}
        </Typography>
        <br />
        <Typography 
          variant="body1" 
          color="text.secondary"
          sx={{
            // Make text selectable, not draggable
            display: 'inline-block',
            userSelect: 'text',
            WebkitAppRegion: 'no-drag',
            '-webkit-app-region': 'no-drag',
          }}
        >
          Here's what's happening with your control panel today
        </Typography>
      </Box>

      {/* Main Stats Grid */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <StatCard
            icon={<People />}
            title="Online Users"
            value={stats.onlineUsers}
            subtitle={stats.totalUsers > 0 ? `of ${stats.totalUsers} total` : 'No data'}
            color={theme.palette.primary.main}
          />
        </Grid>
        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <StatCard
            icon={<AccessTime />}
            title="Last Check-in"
            value={getTimeSinceCheckIn()}
            subtitle={stats.lastCheckIn ? format(stats.lastCheckIn, 'h:mm a') : 'No check-ins'}
            color={theme.palette.secondary.main}
          />
        </Grid>
        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <StatCard
            icon={<Message />}
            title="Active Chats"
            value={stats.activeChats}
            subtitle={stats.todayMessages > 0 ? `${stats.todayMessages} messages today` : 'No messages today'}
            color={theme.palette.info.main}
          />
        </Grid>
        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <StatCard
            icon={<Support />}
            title="Open Tickets"
            value={stats.tickets?.open || stats.pendingTickets || 0}
            subtitle={stats.tickets ? `${stats.tickets.total} total tickets` : `Avg response: ${stats.responseTime}min`}
            color={theme.palette.warning.main}
            trend={stats.tickets?.inProgress ? `${stats.tickets.inProgress} in progress` : undefined}
          />
        </Grid>
      </Grid>

      {/* Ticket Analytics */}
      {stats.tickets && (
        <Grid container spacing={3} sx={{ mb: 4 }}>
          <Grid size={{ xs: 12, md: 6 }}>
            <GlassCard>
              <Box sx={{ p: 3 }}>
                <Typography variant="h6" fontWeight={600} sx={{ mb: 2 }}>
                  Tickets by Status
                </Typography>
                <ResponsiveContainer width="100%" height={250}>
                  <PieChart>
                    <Pie
                      data={[
                        { name: 'Open', value: stats.tickets.open, color: theme.palette.warning.main },
                        { name: 'In Progress', value: stats.tickets.inProgress, color: theme.palette.info.main },
                        { name: 'Closed', value: stats.tickets.closed, color: theme.palette.success.main },
                      ]}
                      cx="50%"
                      cy="50%"
                      labelLine={false}
                      label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
                      outerRadius={80}
                      fill="#8884d8"
                      dataKey="value"
                    >
                      {[
                        { name: 'Open', value: stats.tickets.open, color: theme.palette.warning.main },
                        { name: 'In Progress', value: stats.tickets.inProgress, color: theme.palette.info.main },
                        { name: 'Closed', value: stats.tickets.closed, color: theme.palette.success.main },
                      ].map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={entry.color} />
                      ))}
                    </Pie>
                    <RechartsTooltip />
                    <Legend />
                  </PieChart>
                </ResponsiveContainer>
              </Box>
            </GlassCard>
          </Grid>
          
          <Grid size={{ xs: 12, md: 6 }}>
            <GlassCard>
              <Box sx={{ p: 3 }}>
                <Typography variant="h6" fontWeight={600} sx={{ mb: 2 }}>
                  Tickets by Priority
                </Typography>
                <ResponsiveContainer width="100%" height={250}>
                  <BarChart
                    data={Object.entries(stats.tickets.byPriority || {}).map(([priority, count]) => ({
                      priority: priority.charAt(0).toUpperCase() + priority.slice(1),
                      count,
                    }))}
                    margin={{ top: 20, right: 30, left: 20, bottom: 5 }}
                  >
                    <CartesianGrid strokeDasharray="3 3" stroke={alpha(theme.palette.divider, 0.1)} />
                    <XAxis dataKey="priority" tick={{ fill: theme.palette.text.secondary }} />
                    <YAxis tick={{ fill: theme.palette.text.secondary }} />
                    <RechartsTooltip
                      contentStyle={{
                        backgroundColor: alpha(theme.palette.background.paper, 0.95),
                        borderColor: theme.palette.divider,
                        borderRadius: 8,
                      }}
                    />
                    <Bar dataKey="count" fill={theme.palette.primary.main} radius={[8, 8, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </Box>
            </GlassCard>
          </Grid>
          
          {/* Top Clients by Tickets */}
          {Object.keys(stats.tickets.byClient || {}).length > 0 && (
            <Grid size={{ xs: 12 }}>
              <GlassCard>
                <Box sx={{ p: 3 }}>
                  <Typography variant="h6" fontWeight={600} sx={{ mb: 2 }}>
                    Top Clients by Open Tickets
                  </Typography>
                  <ResponsiveContainer width="100%" height={200}>
                    <BarChart
                      data={Object.entries(stats.tickets.byClient || {})
                        .sort(([, a], [, b]) => (b as number) - (a as number))
                        .slice(0, 10)
                        .map(([client, count]) => ({
                          client: client.length > 15 ? client.substring(0, 15) + '...' : client,
                          tickets: count,
                        }))}
                      layout="horizontal"
                      margin={{ top: 5, right: 30, left: 20, bottom: 5 }}
                    >
                      <CartesianGrid strokeDasharray="3 3" stroke={alpha(theme.palette.divider, 0.1)} />
                      <XAxis dataKey="client" tick={{ fill: theme.palette.text.secondary, fontSize: 11 }} angle={-45} textAnchor="end" height={60} />
                      <YAxis tick={{ fill: theme.palette.text.secondary }} />
                      <RechartsTooltip
                        contentStyle={{
                          backgroundColor: alpha(theme.palette.background.paper, 0.95),
                          borderColor: theme.palette.divider,
                          borderRadius: 8,
                        }}
                      />
                      <Bar dataKey="tickets" fill={theme.palette.secondary.main} radius={[8, 8, 0, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                </Box>
              </GlassCard>
            </Grid>
          )}
        </Grid>
      )}

      <Grid container spacing={3}>
        {/* Online Users List */}
        <Grid size={{ xs: 12, md: 8 }}>
          <GlassCard>
            <Box sx={{ p: 3 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3 }}>
                <Typography variant="h6" fontWeight={600}>
                  Recently Active Users
                </Typography>
                <IconButton size="small">
                  <Refresh />
                </IconButton>
              </Box>

              <Stack spacing={2}>
                {recentUsers.length === 0 ? (
                  <Paper
                    sx={{
                      p: 4,
                      textAlign: 'center',
                      backgroundColor: alpha(theme.palette.background.paper, 0.5),
                      backdropFilter: 'blur(10px)',
                      borderRadius: 2,
                      border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
                    }}
                  >
                    <Typography variant="body2" color="text.secondary">
                      No active users at the moment
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      Users will appear here when they come online
                    </Typography>
                  </Paper>
                ) : recentUsers.map((user, index) => (
                  <motion.div
                    key={index}
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: index * 0.1 }}
                  >
                    <Paper
                      sx={{
                        p: 2,
                        backgroundColor: alpha(theme.palette.background.paper, 0.5),
                        backdropFilter: 'blur(10px)',
                        borderRadius: 2,
                        border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
                        transition: 'all 0.3s',
                        '&:hover': {
                          transform: 'translateX(4px)',
                          boxShadow: theme.shadows[4],
                        },
                      }}
                    >
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                        <Badge
                          overlap="circular"
                          anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                          badgeContent={
                            <Circle
                              sx={{
                                fontSize: 12,
                                color: user.status === 'online' 
                                  ? theme.palette.success.main 
                                  : user.status === 'away'
                                  ? theme.palette.warning.main
                                  : theme.palette.action.disabled,
                              }}
                            />
                          }
                        >
                          <Avatar sx={{ bgcolor: theme.palette.primary.main }}>
                            {user.avatar}
                          </Avatar>
                        </Badge>
                        <Box sx={{ flex: 1 }}>
                          <Typography variant="body2" fontWeight={600}>
                            {user.name}
                          </Typography>
                          <Typography variant="caption" color="text.secondary" component="div">
                            {user.email}
                          </Typography>
                          {user.company && (
                            <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.65rem' }}>
                              {user.company}
                            </Typography>
                          )}
                        </Box>
                        <Chip
                          label={user.status}
                          size="small"
                          sx={{
                            backgroundColor: alpha(
                              user.status === 'online' 
                                ? theme.palette.success.main 
                                : user.status === 'away'
                                ? theme.palette.warning.main
                                : theme.palette.action.disabled,
                              0.1
                            ),
                            color: user.status === 'online' 
                              ? theme.palette.success.main 
                              : user.status === 'away'
                              ? theme.palette.warning.main
                              : theme.palette.text.secondary,
                          }}
                        />
                      </Box>
                    </Paper>
                  </motion.div>
                ))}
              </Stack>

              {stats.onlineUsers > 5 && (
                <Box sx={{ mt: 3, textAlign: 'center' }}>
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                    And {stats.onlineUsers - recentUsers.length} more users online
                  </Typography>
                </Box>
              )}
            </Box>
          </GlassCard>
        </Grid>

        {/* System Status */}
        <Grid size={{ xs: 12, md: 4 }}>
          <Stack spacing={3}>
            {/* Uptime */}
            <GlassCard>
              <Box sx={{ p: 3 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                  <SignalCellularAlt sx={{ color: theme.palette.success.main }} />
                  <Typography variant="h6" fontWeight={600}>
                    System Status
                  </Typography>
                </Box>
                
                <Box sx={{ mb: 3 }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                    <Typography variant="body2">Uptime</Typography>
                    <Typography variant="body2" fontWeight={600} color="success.main">
                      {stats.uptime > 0 ? `${stats.uptime}%` : '--'}
                    </Typography>
                  </Box>
                  <LinearProgress
                    variant="determinate"
                    value={stats.uptime || 0}
                    sx={{
                      height: 8,
                      borderRadius: 4,
                      backgroundColor: alpha(theme.palette.success.main, 0.1),
                      '& .MuiLinearProgress-bar': {
                        borderRadius: 4,
                        background: `linear-gradient(90deg, ${theme.palette.success.main} 0%, ${theme.palette.success.light} 100%)`,
                      },
                    }}
                  />
                </Box>

                <Stack spacing={2}>
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <CheckCircle sx={{ fontSize: 16, color: theme.palette.success.main }} />
                      <Typography variant="body2">API Server</Typography>
                    </Box>
                    <Chip label="Operational" size="small" color="success" />
                  </Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <CheckCircle sx={{ fontSize: 16, color: theme.palette.success.main }} />
                      <Typography variant="body2">WebSocket</Typography>
                    </Box>
                    <Chip 
                      label={isConnected ? "Connected" : "Disconnected"} 
                      size="small" 
                      color={isConnected ? "success" : "error"} 
                    />
                  </Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <CheckCircle sx={{ fontSize: 16, color: theme.palette.success.main }} />
                      <Typography variant="body2">Database</Typography>
                    </Box>
                    <Chip label="Healthy" size="small" color="success" />
                  </Box>
                </Stack>
              </Box>
            </GlassCard>

            {/* Quick Actions */}
            <GlassCard>
              <Box sx={{ p: 3 }}>
                <Typography variant="h6" fontWeight={600} sx={{ mb: 2 }}>
                  Quick Stats
                </Typography>
                <Stack spacing={2}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                    <Typography variant="body2" color="text.secondary">Response Time</Typography>
                    <Typography variant="body2" fontWeight={600}>{stats.responseTime > 0 ? `${stats.responseTime} min` : '--'}</Typography>
                  </Box>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                    <Typography variant="body2" color="text.secondary">Messages Today</Typography>
                    <Typography variant="body2" fontWeight={600}>{stats.todayMessages}</Typography>
                  </Box>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                    <Typography variant="body2" color="text.secondary">Active Sessions</Typography>
                    <Typography variant="body2" fontWeight={600}>{users ? users.filter((u: AgentUser) => u.isOnline).length : 0}</Typography>
                  </Box>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                    <Typography variant="body2" color="text.secondary">Pending Tasks</Typography>
                    <Typography variant="body2" fontWeight={600}>{stats.tickets?.open || stats.pendingTickets || 0}</Typography>
                  </Box>
                  {stats.clients !== undefined && (
                    <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                      <Typography variant="body2" color="text.secondary">Total Clients</Typography>
                      <Typography variant="body2" fontWeight={600}>{stats.clients}</Typography>
                    </Box>
                  )}
                </Stack>
              </Box>
            </GlassCard>
          </Stack>
        </Grid>
      </Grid>
    </Container>
  );
};

export default Dashboard;